import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

export default function Mikrotik() {
  const { usuario } = useAuth();
  const [config, setConfig] = useState({ host:'', puerto:8728, usuario:'admin', password:'', ssl:false, activo:false });
  const [conectados, setConectados] = useState({ pppoe:[], queues:[] });
  const [tab, setTab] = useState('monitor');
  const [probando, setProbando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [importando, setImportando] = useState(false);
  const [resultImport, setResultImport] = useState(null);
  const [respaldo, setRespaldo] = useState(null);   // la copia guardada antes de borrar
  const [msgConexion, setMsgConexion] = useState(null);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');

  useEffect(() => {
    api.get('/api/mikrotik/config').then(r => {
      if (r.data) setConfig(prev => ({
        ...prev, ...r.data, password:'',
        puerto: parseInt(r.data.puerto || r.data.port || 8728) || 8728,
      }));
    }).catch(() => {});
  }, []);

  const cargarConectados = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await api.get('/api/mikrotik/conectados');
      setConectados(data);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo conectar al MikroTik');
    } finally { setCargando(false); }
  }, []);

  useEffect(() => { if (tab === 'monitor' && config.activo) cargarConectados(); }, [tab, config.activo]);

  const probar = async () => {
    setProbando(true); setMsgConexion(null);
    try {
      const { data } = await api.post('/api/mikrotik/probar', { ...config, puerto: parseInt(config.puerto) || 8728 });
      setMsgConexion({ ok: true, texto: data.mensaje });
    } catch (err) {
      setMsgConexion({ ok: false, texto: err.response?.data?.error || 'Error de conexión' });
    } finally { setProbando(false); }
  };

  const guardar = async () => {
    setGuardando(true); setError(''); setOk('');
    try {
      await api.put('/api/mikrotik/config', { ...config, puerto: parseInt(config.puerto) || 8728 });
      setOk('Configuración guardada correctamente');
      setTimeout(() => setOk(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar');
    } finally { setGuardando(false); }
  };

  const ejecutar = async (clienteId, accion, nombre) => {
    if (!window.confirm(`¿${accion === 'suspender' ? 'Suspender' : 'Activar'} a ${nombre} en MikroTik?`)) return;
    try {
      const { data } = await api.post(`/api/mikrotik/accion/${clienteId}`, { accion });
      alert(data.mensaje);
      cargarConectados();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al ejecutar acción');
    }
  };

  const totalConectados = conectados.pppoe.length + conectados.queues.filter(q => q.activo).length;

  const verRespaldo = async () => {
    try {
      const { data } = await api.get('/api/mikrotik/respaldo');
      setRespaldo(data);
    } catch (_) { setRespaldo(null); }   // sin permiso o sin tabla: no se muestra nada
  };

  useEffect(() => { verRespaldo(); }, []);

  // Deshacer el borrado. Lo creado después se pierde: es volver atrás, no mezclar.
  const devolverRespaldo = async () => {
    const r = respaldo?.respaldo;
    const cuando = r?.hecho_en ? new Date(r.hecho_en).toLocaleString('es-CO') : '';
    if (!window.confirm(
      [`Devolver ${r?.clientes || 0} clientes y ${r?.pagos || 0} pagos como estaban el ${cuando}.`,
       '',
       `Los ${respaldo?.clientes_ahora || 0} clientes que hay ahora se reemplazan: ` +
       'lo creado después de esa fecha se pierde.',
       '',
       '¿Seguir?'].join('\n'))) return;
    setImportando(true);
    try {
      const { data } = await api.post('/api/mikrotik/respaldo/devolver');
      setResultImport({ mensaje: data.mensaje });
      verRespaldo();
    } catch (err) {
      setResultImport({ error: err.response?.data?.error || 'No se pudo devolver' });
    } finally { setImportando(false); }
  };

  const importarClientes = async (limpiar=false) => {
    // El aviso dice QUÉ se pierde, no solo que se borra: el router no puede devolver nada
    // de esto y quien le da al botón tiene que saberlo antes, no después.
    const msg = limpiar
      ? ['⚠️ BORRAR TODOS los clientes y volver a crearlos desde el MikroTik.',
         '',
         'El router solo sabe el usuario, el perfil y la velocidad. NO vuelven:',
         '  · dirección, teléfono y cédula',
         '  · coordenadas y fecha de inscripción',
         '  · a quién no se le cobra',
         '  · de qué caja o antena cuelga cada uno',
         '  · todo el historial de pagos',
         '',
         'Se guarda una copia antes de borrar y se puede devolver con «Devolver la copia».',
         '',
         '¿Seguir?'].join('\n')
      : '¿Importar usuarios PPPoE y colas del MikroTik como clientes? Los que ya existen no se duplicarán.';
    if (!window.confirm(msg)) return;
    setImportando(true); setResultImport(null);
    try {
      const { data } = await api.post('/api/mikrotik/importar-clientes', { limpiar });
      setResultImport(data);
      verRespaldo();
    } catch (err) {
      setResultImport({ error: err.response?.data?.error || 'Error al importar' });
    } finally { setImportando(false); }
  };

  return (
    <Layout>
      <div style={s.header}>
        <div>
          <h1 style={s.titulo}>MikroTik</h1>
          <p style={s.sub}>Gestión de red y clientes conectados</p>
        </div>
        <div style={s.estadoConexion}>
          <span style={{ ...s.estadoDot, background: config.activo ? '#10B981' : '#6B7280' }} />
          <span style={{ fontSize:'13px', color: config.activo ? '#10B981' : 'rgba(255,255,255,0.4)' }}>
            {config.activo ? `Conectado — ${config.host}` : 'No configurado'}
          </span>
        </div>
      </div>

      <div style={s.tabs}>
        <button onClick={() => setTab('monitor')} style={{ ...s.tab, ...(tab==='monitor' ? s.tabActivo : {}) }}>
          📡 Monitor en tiempo real
        </button>
        <button onClick={() => setTab('config')} style={{ ...s.tab, ...(tab==='config' ? s.tabActivo : {}) }}>
          ⚙ Configuración
        </button>
      </div>

      {tab === 'monitor' && (
        <>
          <div style={s.monitorHeader}>
            <div style={s.monitorStats}>
              <div style={s.monitorStat}>
                <span style={{ fontSize:'28px', fontWeight:'700', color:'#00E5FF' }}>{totalConectados}</span>
                <span style={s.monitorStatLabel}>clientes conectados</span>
              </div>
              <div style={s.monitorStat}>
                <span style={{ fontSize:'22px', fontWeight:'700', color:'#6366F1' }}>{conectados.pppoe.length}</span>
                <span style={s.monitorStatLabel}>PPPoE activos</span>
              </div>
              <div style={s.monitorStat}>
                <span style={{ fontSize:'22px', fontWeight:'700', color:'#10B981' }}>{conectados.queues.filter(q=>q.activo).length}</span>
                <span style={s.monitorStatLabel}>Queues activos</span>
              </div>
            </div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:'10px' }}>
            <button style={s.btnImportar} onClick={()=>importarClientes(false)} disabled={importando}>
              {importando ? '⟳ Importando...' : '⬇ Importar clientes nuevos'}
            </button>
            <button style={{...s.btnImportar, background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.25)', color:'#FCA5A5'}} 
              onClick={()=>importarClientes(true)} disabled={importando}>
              🔄 Reimportar desde cero
            </button>
            {respaldo?.respaldo?.clientes > 0 && (
              <button style={{...s.btnImportar, background:'rgba(251,191,36,0.1)',
                border:'1px solid rgba(251,191,36,0.3)', color:'#FDE047'}}
                onClick={devolverRespaldo} disabled={importando}
                title={`Copia del ${new Date(respaldo.respaldo.hecho_en).toLocaleString('es-CO')}`}>
                ↩ Devolver la copia ({respaldo.respaldo.clientes})
              </button>
            )}
            <button style={s.btnRefresh} onClick={cargarConectados} disabled={cargando}>
              {cargando ? '⟳ Actualizando...' : '⟳ Actualizar'}
            </button>
          </div>
          </div>

          {resultImport && (
            <div style={{ ...s.errorBox, background: resultImport.error ? 'rgba(239,68,68,0.08)' : 'rgba(16,185,129,0.08)', borderColor: resultImport.error ? 'rgba(239,68,68,0.2)' : 'rgba(16,185,129,0.2)', color: resultImport.error ? '#FCA5A5' : '#6EE7B7', marginBottom:'16px' }}>
              {resultImport.error ? `❌ ${resultImport.error}` : `✅ ${resultImport.mensaje}`}
              {resultImport.errores?.length > 0 && (
                <div style={{ marginTop:'8px', fontSize:'11px', opacity:0.7 }}>
                  {resultImport.errores.map((e,i) => <div key={i}>• {e}</div>)}
                </div>
              )}
            </div>
          )}

          {error && <div style={s.errorBox}>⚠ {error}</div>}

          {!config.activo ? (
            <div style={s.noConfig}>
              <div style={{ fontSize:'40px', marginBottom:'12px' }}>📡</div>
              <div style={{ fontSize:'16px', fontWeight:'600', marginBottom:'8px' }}>MikroTik no configurado</div>
              <div style={{ fontSize:'13px', color:'rgba(255,255,255,0.4)', marginBottom:'20px' }}>Configura la conexión en la pestaña "Configuración"</div>
              <button style={s.btnPrimario} onClick={() => setTab('config')}>⚙ Ir a Configuración</button>
            </div>
          ) : (
            <>
              {/* PPPoE */}
              {conectados.pppoe.length > 0 && (
                <>
                  <h3 style={s.seccionTitulo}>🔵 Clientes PPPoE conectados ({conectados.pppoe.length})</h3>
                  <div style={s.tabla}>
                    <table style={s.table}>
                      <thead><tr style={s.thead}>
                        <th style={s.th}>Usuario PPPoE</th>
                        <th style={s.th}>IP</th>
                        <th style={s.th}>Tiempo conectado</th>
                        <th style={s.th}>Cliente en sistema</th>
                        <th style={s.th}>Acciones</th>
                      </tr></thead>
                      <tbody>
                        {conectados.pppoe.map((p, i) => (
                          <tr key={i} style={s.tr}>
                            <td style={s.td}><span style={s.usuarioTag}>{p.usuario}</span></td>
                            <td style={s.td}><span style={s.ipTag}>{p.ip || '—'}</span></td>
                            <td style={s.td}><span style={s.tiempoTag}>{p.tiempo}</span></td>
                            <td style={s.td}>
                              {p.cliente
                                ? <span style={s.clienteTag}>👤 {p.cliente.nombre}</span>
                                : <span style={s.sinCliente}>No registrado</span>}
                            </td>
                            <td style={s.td}>
                              {p.cliente && (
                                <button style={s.btnSuspender} onClick={() => ejecutar(p.cliente.id, 'suspender', p.cliente.nombre)}>
                                  ⏸ Suspender
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {/* Simple Queue */}
              {conectados.queues.length > 0 && (
                <>
                  <h3 style={{ ...s.seccionTitulo, marginTop:'20px' }}>📡 Clientes Radio Enlace — Simple Queue ({conectados.queues.length})</h3>
                  <div style={s.tabla}>
                    <table style={s.table}>
                      <thead><tr style={s.thead}>
                        <th style={s.th}>Nombre cola</th>
                        <th style={s.th}>IP / Red</th>
                        <th style={s.th}>Velocidad</th>
                        <th style={s.th}>Estado</th>
                        <th style={s.th}>Cliente en sistema</th>
                        <th style={s.th}>Acciones</th>
                      </tr></thead>
                      <tbody>
                        {conectados.queues.map((q, i) => (
                          <tr key={i} style={s.tr}>
                            <td style={s.td}><span style={s.usuarioTag}>{q.nombre}</span></td>
                            <td style={s.td}><span style={s.ipTag}>{q.target || '—'}</span></td>
                            <td style={s.td}><span style={s.velTag}>{q.velocidad || '—'}</span></td>
                            <td style={s.td}>
                              <span style={{ fontSize:'12px', color: q.activo ? '#10B981':'#EF4444', fontWeight:'600' }}>
                                {q.activo ? '● Activo' : '● Suspendido'}
                              </span>
                            </td>
                            <td style={s.td}>
                              {q.cliente
                                ? <span style={s.clienteTag}>👤 {q.cliente.nombre}</span>
                                : <span style={s.sinCliente}>No registrado</span>}
                            </td>
                            <td style={s.td}>
                              {q.cliente && (
                                <div style={{ display:'flex', gap:'6px' }}>
                                  {(q.velocidad === '64000/64000' || q.velocidad?.includes('64k') || q.cliente.estado === 'suspendido' || q.cliente.estado === 'moroso')
                                    ? usuario?.rol === 'admin' && <button style={s.btnActivar} onClick={() => ejecutar(q.cliente.id, 'activar', q.cliente.nombre)}>▶ Activar</button>
                                    : <button style={s.btnSuspender} onClick={() => ejecutar(q.cliente.id, 'suspender', q.cliente.nombre)}>⏸ Suspender</button>
                                  }
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {totalConectados === 0 && !cargando && (
                <div style={s.vacio}>No hay clientes conectados en este momento</div>
              )}
            </>
          )}
        </>
      )}

      {tab === 'config' && (
        <div style={s.configGrid}>
          <div style={s.configCard}>
            <h3 style={s.cardTitulo}>🔌 Conexión al MikroTik</h3>
            <div style={s.formGrid}>
              <div style={{ ...s.campo, gridColumn:'1/-1' }}>
                <label style={s.label}>IP o dominio del MikroTik *</label>
                <input style={s.input} value={config.host} onChange={e => setConfig({...config, host:e.target.value})} placeholder="Ej: 192.168.1.1 o mikrotik.midominio.com" />
              </div>
              <div style={s.campo}>
                <label style={s.label}>Puerto API</label>
                <input style={s.input} type="number" value={config.puerto} onChange={e => setConfig({...config, puerto:parseInt(e.target.value)})} placeholder="8728" />
                <span style={s.hint}>8728 normal · 8729 con SSL</span>
              </div>
              <div style={s.campo}>
                <label style={s.label}>Usuario</label>
                <input style={s.input} value={config.usuario} onChange={e => setConfig({...config, usuario:e.target.value})} placeholder="admin" />
              </div>
              <div style={s.campo}>
                <label style={s.label}>Contraseña</label>
                <input style={s.input} type="password" value={config.password} onChange={e => setConfig({...config, password:e.target.value})} placeholder="Dejar vacío para no cambiar" />
              </div>
              <div style={s.campo}>
                <label style={s.label}>Opciones</label>
                <div style={s.checkRow}>
                  <label style={s.checkLabel}>
                    <input type="checkbox" checked={config.ssl} onChange={e => setConfig({...config, ssl:e.target.checked})} />
                    Usar SSL (puerto 8729)
                  </label>
                  <label style={s.checkLabel}>
                    <input type="checkbox" checked={config.activo} onChange={e => setConfig({...config, activo:e.target.checked})} />
                    Integración activa
                  </label>
                </div>
              </div>
            </div>

            {msgConexion && (
              <div style={{ ...s.msgBox, borderColor: msgConexion.ok ? 'rgba(16,185,129,0.3)':'rgba(239,68,68,0.3)', background: msgConexion.ok ? 'rgba(16,185,129,0.08)':'rgba(239,68,68,0.08)', color: msgConexion.ok ? '#6EE7B7':'#FCA5A5' }}>
                {msgConexion.ok ? '✅' : '❌'} {msgConexion.texto}
              </div>
            )}
            {error && <div style={s.errorBox}>⚠ {error}</div>}
            {ok && <div style={{ ...s.msgBox, borderColor:'rgba(16,185,129,0.3)', background:'rgba(16,185,129,0.08)', color:'#6EE7B7' }}>✅ {ok}</div>}

            <div style={s.configBtns}>
              <button style={s.btnProbar} onClick={probar} disabled={probando}>{probando ? 'Probando...' : '🔌 Probar conexión'}</button>
              <button style={s.btnPrimario} onClick={guardar} disabled={guardando}>{guardando ? 'Guardando...' : '✓ Guardar configuración'}</button>
            </div>
          </div>

          <div style={s.configCard}>
            <h3 style={s.cardTitulo}>📋 Instrucciones de configuración</h3>
            <div style={s.instrucciones}>
              <div style={s.paso}>
                <span style={s.pasoNum}>1</span>
                <div>
                  <div style={s.pasoTitulo}>Habilitar API en MikroTik</div>
                  <div style={s.pasoDesc}>En Winbox: <code style={s.code}>IP → Services → api → Enable</code></div>
                </div>
              </div>
              <div style={s.paso}>
                <span style={s.pasoNum}>2</span>
                <div>
                  <div style={s.pasoTitulo}>Crear usuario API (recomendado)</div>
                  <div style={s.pasoDesc}><code style={s.code}>System → Users → Add</code><br/>Grupo: <code style={s.code}>full</code> o crear uno con permisos específicos</div>
                </div>
              </div>
              <div style={s.paso}>
                <span style={s.pasoNum}>3</span>
                <div>
                  <div style={s.pasoTitulo}>Permitir acceso desde tu servidor</div>
                  <div style={s.pasoDesc}><code style={s.code}>IP → Firewall → Filter</code><br/>Permitir puerto 8728 desde la IP del servidor</div>
                </div>
              </div>
              <div style={s.paso}>
                <span style={s.pasoNum}>4</span>
                <div>
                  <div style={s.pasoTitulo}>Configurar clientes en el sistema</div>
                  <div style={s.pasoDesc}>En cada cliente, agrega su usuario PPPoE o nombre de la cola en el campo "MikroTik"</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

const s = {
  header: { display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap: 'wrap', gap: '12px', marginBottom:'24px' },
  titulo: { fontSize:'24px', fontWeight:'700', marginBottom:'4px' },
  sub: { color:'rgba(255,255,255,0.4)', fontSize:'14px' },
  estadoConexion: { display:'flex', alignItems:'center', gap:'8px', background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'10px', padding:'8px 14px' },
  estadoDot: { width:'8px', height:'8px', borderRadius:'50%', flexShrink:0 },
  tabs: { display:'flex', gap:'8px', marginBottom:'20px', borderBottom:'1px solid rgba(255,255,255,0.08)' },
  tab: { background:'none', border:'none', borderBottom:'2px solid transparent', padding:'10px 16px', color:'rgba(255,255,255,0.4)', fontSize:'14px', cursor:'pointer', marginBottom:'-1px' },
  tabActivo: { color:'#00E5FF', borderBottomColor:'#00E5FF' },
  monitorHeader: { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px' },
  monitorStats: { display:'flex', gap:'32px' },
  monitorStat: { display:'flex', flexDirection:'column', gap:'4px' },
  monitorStatLabel: { fontSize:'12px', color:'rgba(255,255,255,0.4)' },
  btnRefresh: { background:'rgba(0,229,255,0.1)', border:'1px solid rgba(0,229,255,0.25)', borderRadius:'8px', padding:'8px 16px', color:'#00E5FF', fontSize:'13px', cursor:'pointer', fontWeight:'600' },
  btnImportar: { maxWidth:'100%', background:'rgba(16,185,129,0.1)', border:'1px solid rgba(16,185,129,0.25)', borderRadius:'8px', padding:'8px 16px', color:'#6EE7B7', fontSize:'13px', cursor:'pointer', fontWeight:'600' },
  btnPrimario: { background:'linear-gradient(135deg,#00E5FF,#6366F1)', border:'none', borderRadius:'10px', padding:'10px 20px', color:'#fff', fontSize:'14px', fontWeight:'600', cursor:'pointer' },
  btnProbar: { background:'rgba(99,102,241,0.12)', border:'1px solid rgba(99,102,241,0.3)', borderRadius:'10px', padding:'10px 16px', color:'#A5B4FC', fontSize:'13px', cursor:'pointer', fontWeight:'600' },
  seccionTitulo: { fontSize:'14px', fontWeight:'600', color:'rgba(255,255,255,0.7)', marginBottom:'12px' },
  tabla: { background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'12px', overflow:'hidden', marginBottom:'8px' },
  table: { width:'100%', borderCollapse:'collapse' },
  thead: { background:'rgba(255,255,255,0.04)' },
  th: { padding:'10px 14px', textAlign:'left', fontSize:'11px', color:'rgba(255,255,255,0.4)', fontWeight:'600', textTransform:'uppercase' },
  tr: { borderTop:'1px solid rgba(255,255,255,0.05)' },
  td: { padding:'11px 14px', verticalAlign:'middle', fontSize:'13px' },
  vacio: { padding:'60px', textAlign:'center', color:'rgba(255,255,255,0.3)', fontSize:'14px' },
  noConfig: { textAlign:'center', padding:'60px', color:'rgba(255,255,255,0.5)' },
  usuarioTag: { fontFamily:'monospace', fontSize:'13px', color:'#00E5FF' },
  ipTag: { fontFamily:'monospace', fontSize:'12px', color:'rgba(255,255,255,0.6)' },
  tiempoTag: { fontSize:'12px', color:'rgba(255,255,255,0.5)' },
  velTag: { fontSize:'12px', color:'#10B981', fontFamily:'monospace' },
  clienteTag: { fontSize:'12px', color:'rgba(255,255,255,0.7)' },
  sinCliente: { fontSize:'11px', color:'rgba(255,255,255,0.25)', fontStyle:'italic' },
  btnSuspender: { background:'rgba(239,68,68,0.12)', border:'none', borderRadius:'6px', padding:'5px 10px', color:'#FCA5A5', fontSize:'12px', cursor:'pointer', fontWeight:'600' },
  btnActivar: { background:'rgba(16,185,129,0.12)', border:'none', borderRadius:'6px', padding:'5px 10px', color:'#6EE7B7', fontSize:'12px', cursor:'pointer', fontWeight:'600' },
  errorBox: { background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:'8px', padding:'10px 14px', color:'#FCA5A5', fontSize:'13px', marginBottom:'16px' },
  configGrid: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px' },
  configCard: { background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'16px', padding:'20px' },
  cardTitulo: { fontSize:'14px', fontWeight:'600', marginBottom:'16px', color:'rgba(255,255,255,0.8)' },
  formGrid: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'16px' },
  campo: { display:'flex', flexDirection:'column', gap:'6px' },
  label: { color:'rgba(255,255,255,0.6)', fontSize:'13px' },
  input: { background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'8px', padding:'9px 12px', color:'#fff', fontSize:'13px', outline:'none' },
  hint: { fontSize:'11px', color:'rgba(255,255,255,0.3)' },
  checkRow: { display:'flex', flexDirection:'column', gap:'8px', marginTop:'4px' },
  checkLabel: { display:'flex', alignItems:'center', gap:'8px', fontSize:'13px', color:'rgba(255,255,255,0.6)', cursor:'pointer' },
  msgBox: { border:'1px solid', borderRadius:'8px', padding:'10px 14px', fontSize:'13px', marginBottom:'14px' },
  configBtns: { display:'flex', gap:'10px', justifyContent:'flex-end' },
  instrucciones: { display:'flex', flexDirection:'column', gap:'14px' },
  paso: { display:'flex', gap:'12px', alignItems:'flex-start' },
  pasoNum: { width:'24px', height:'24px', borderRadius:'50%', background:'rgba(0,229,255,0.15)', color:'#00E5FF', fontSize:'12px', fontWeight:'700', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 },
  pasoTitulo: { fontSize:'13px', fontWeight:'600', marginBottom:'4px' },
  pasoDesc: { fontSize:'12px', color:'rgba(255,255,255,0.5)', lineHeight:'1.6' },
  code: { background:'rgba(255,255,255,0.08)', padding:'1px 6px', borderRadius:'4px', fontFamily:'monospace', fontSize:'11px' },
};
